import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { useEffect, useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { DvfField, dvfExtent, dvfMaxMagnitude } from '~/domain/dvf/dvfField';
import { DvfDisplayRangeMm } from '~/domain/dvf/dvfDisplayRange';
import { paneImageOf } from '~/engine/view/paneImage';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { usePlaneRedrawTick } from '~/hooks/viewer/annotationOverlay';
import { useSelectedDeformationField } from '~/hooks/viewer/useSelectedDeformationField';
import { deformationHeatmapEnabledState } from '~/state/atoms/deformationDisplayAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    clearPlaneCanvas,
    PLANE_OVERLAY_CANVAS_STYLE,
    PLANE_OVERLAY_REDRAW_EVENTS,
    planeLattice,
} from './planeCanvas';
import { paintHeatmap } from './dvfHeatmap';

// The sampled slice, kept until the slice or field changes: zoom and pan only re-blit it.
interface PaintedSlice {
    readonly key: string;
    readonly image: HTMLCanvasElement;
    // Lattice corners, to recompute the canvas transform at the current zoom/pan.
    readonly origin: ReadonlyVec3;
    readonly colStep: ReadonlyVec3;
    readonly rowStep: ReadonlyVec3;
}

function paintSlice(
    view: PlaneView,
    field: DvfField,
    range: DvfDisplayRangeMm,
    key: string
): PaintedSlice | null {
    const pitchMm = Math.max(...field.spacing);
    const { points, uCount, vCount } = planeLattice(
        view,
        field,
        pitchMm,
        dvfExtent(field)
    );
    if (uCount < 1 || vCount < 1) {
        return null;
    }
    const image = document.createElement('canvas');
    image.width = uCount;
    image.height = vCount;
    const ctx = image.getContext('2d');
    if (!ctx) {
        return null;
    }
    const pixels = ctx.createImageData(uCount, vCount);
    paintHeatmap(pixels.data, points, field, range);
    ctx.putImageData(pixels, 0, 0);
    const colStep =
        uCount > 1
            ? vec3.subtract(vec3.create(), points[1], points[0])
            : vec3.scale(vec3.create(), view.viewXDirection, pitchMm);
    const rowStep =
        vCount > 1
            ? vec3.subtract(vec3.create(), points[uCount], points[0])
            : vec3.scale(vec3.create(), view.viewYDirection, pitchMm);
    return { key, image, origin: points[0], colStep, rowStep };
}

function blit(
    ctx: CanvasRenderingContext2D,
    view: PlaneView,
    slice: PaintedSlice
): void {
    const o = view.canvasCoordsFromPatientCoords(slice.origin);
    const c = view.canvasCoordsFromPatientCoords(
        vec3.add(vec3.create(), slice.origin, slice.colStep)
    );
    const r = view.canvasCoordsFromPatientCoords(
        vec3.add(vec3.create(), slice.origin, slice.rowStep)
    );
    const ax = c[0] - o[0];
    const ay = c[1] - o[1];
    const bx = r[0] - o[0];
    const by = r[1] - o[1];
    // Each pixel is centred on its lattice point, so shift the origin back half a cell.
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.transform(
        ax,
        ay,
        bx,
        by,
        o[0] - ax / 2 - bx / 2,
        o[1] - ay / 2 - by / 2
    );
    ctx.drawImage(slice.image, 0, 0);
    ctx.restore();
}

export default function DeformationHeatmapOverlay({
    view,
}: {
    view: PlaneView | null;
}) {
    const enabled = useRecoilValue(deformationHeatmapEnabledState);
    const field = useSelectedDeformationField();
    const viewManager = useRecoilValue(viewManagerState);
    const tick = usePlaneRedrawTick(viewManager, PLANE_OVERLAY_REDRAW_EVENTS);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const sliceRef = useRef<PaintedSlice | null>(null);

    // Coloured across the full field, zero to its largest displacement.
    const range = useMemo(
        () =>
            field ? { lowMm: 0, highMm: dvfMaxMagnitude(field) } : undefined,
        [field]
    );

    useEffect(() => {
        const ctx = clearPlaneCanvas(canvasRef.current);
        if (
            !ctx ||
            !view ||
            !enabled ||
            !field ||
            !range ||
            paneImageOf(view).kind !== 'primary'
        ) {
            sliceRef.current = null;
            return;
        }
        const key = `${view.label}:${view.planeCoordinates}:${range.highMm}`;
        if (sliceRef.current?.key !== key) {
            sliceRef.current = paintSlice(view, field, range, key);
        }
        if (sliceRef.current) {
            blit(ctx, view, sliceRef.current);
        }
    }, [tick, view, enabled, field, range]);

    useEffect(() => {
        sliceRef.current = null;
    }, [field]);

    if (!enabled || !field) {
        return null;
    }
    return (
        <canvas
            ref={canvasRef}
            style={{ ...PLANE_OVERLAY_CANVAS_STYLE, zIndex: 5 }}
            data-testid="deformation-heatmap-overlay"
        />
    );
}
